// /dealeri/:alias/anunturi — full filterable list of active listings for a dealer.
// Uses public.dealer_page_listings RPC. Reuses site Header/Footer + SEOFooterContent.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { SEOFooterContent } from "@/components/seo/SEOFooterContent";
import { ModernListingCard } from "@/components/listings/card/ModernListingCard";
import { DealerStockFilters, type StockFilters } from "@/components/dealers/DealerStockFilters";
import { buildListingSlug } from "@/utils/listingSlug";
import { setAssistantDealerContext } from "@/utils/assistantContext";

interface DealerRow {
  id: string;
  user_id: string;
  subdomain_alias: string | null;
  company_name: string | null;
  showroom_name: string | null;
  city: string | null;
}

interface ListingRow {
  id: string;
  title: string | null;
  price: number | null;
  make: string | null;
  model: string | null;
  year: number | null;
  mileage_value: number | null;
  mileage_unit: string | null;
  fuel_type: string | null;
  transmission: string | null;
  location_city: string | null;
  photos: any;
  created_at: string | null;
}

const PAGE_LIMIT = 24;

export default function DealerPublicListings() {
  const { alias = "" } = useParams<{ alias: string }>();
  const [dealer, setDealer] = useState<DealerRow | null>(null);
  const [listings, setListings] = useState<ListingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [filters, setFilters] = useState<StockFilters>({ fuel: "all", sort: "newest" });
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      const [{ data: dRows }, { data: lRows }] = await Promise.all([
        supabase.rpc("get_dealer_by_alias", { _alias: alias }),
        supabase.rpc("dealer_page_listings", { _alias: alias, _limit: PAGE_LIMIT, _offset: 0 }),
      ]);
      if (cancelled) return;
      const d = Array.isArray(dRows) && dRows.length > 0 ? (dRows[0] as DealerRow) : null;
      if (!d) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setDealer(d);
      const rows = Array.isArray(lRows) ? (lRows as ListingRow[]) : [];
      setListings(rows);
      setHasMore(rows.length === PAGE_LIMIT);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [alias]);

  // Informează asistentul AI despre dealerul vizionat (varianta cu datele
  // disponibile pe pagina de anunțuri).
  useEffect(() => {
    if (!dealer) {
      setAssistantDealerContext(null);
      return;
    }
    const makeSet = new Set<string>();
    listings.forEach((l) => l.make && makeSet.add(l.make));
    setAssistantDealerContext({
      id: dealer.id,
      name: dealer.company_name || dealer.showroom_name || alias,
      city: dealer.city,
      activeListings: listings.length,
      makes: Array.from(makeSet).join(", ") || null,
    });
    return () => setAssistantDealerContext(null);
  }, [dealer, listings, alias]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const { data } = await supabase.rpc("dealer_page_listings", {
      _alias: alias,
      _limit: PAGE_LIMIT,
      _offset: listings.length,
    });
    const rows = Array.isArray(data) ? (data as ListingRow[]) : [];
    setListings((prev) => [...prev, ...rows]);
    setHasMore(rows.length === PAGE_LIMIT);
    setLoadingMore(false);
  };

  const fuelOptions = useMemo(() => {
    const set = new Set<string>();
    listings.forEach((l) => l.fuel_type && set.add(l.fuel_type));
    return Array.from(set).sort();
  }, [listings]);

  const filtered = useMemo(() => {
    let out = listings.slice();
    if (filters.fuel !== "all") out = out.filter((l) => l.fuel_type === filters.fuel);
    switch (filters.sort) {
      case "price_asc":
        out.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
        break;
      case "price_desc":
        out.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
        break;
      case "year_desc":
        out.sort((a, b) => Number(b.year || 0) - Number(a.year || 0));
        break;
      case "km_asc":
        out.sort((a, b) => Number(a.mileage_value || 0) - Number(b.mileage_value || 0));
        break;
      default:
        out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return out;
  }, [listings, filters]);

  const displayName = dealer?.company_name || dealer?.showroom_name || alias;
  const title = dealer ? `Anunțuri ${displayName} | RO Auto Market` : "Anunțuri dealer | RO Auto Market";
  const description = `Toate anunțurile active ale dealerului ${displayName}${dealer?.city ? `, ${dealer.city}` : ""} pe RO Auto Market.`;
  const canonical = `https://roautomarket.ro/dealeri/${alias}/anunturi`;

  if (notFound) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <Helmet>
          <title>Dealer nu a fost găsit | RO Auto Market</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <h1 className="text-2xl font-serif mb-3">Dealerul nu a fost găsit</h1>
        <Link to="/dealeri-verificati" className="inline-block mt-6 text-primary underline">
          Vezi toți dealerii verificați
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <section className="container mx-auto px-4 py-10">
        <nav className="text-sm text-muted-foreground mb-3">
          <Link to={`/dealeri/${alias}`} className="hover:underline">← {displayName}</Link>
        </nav>
        <h1 className="text-2xl md:text-3xl font-serif font-medium mb-1">
          Anunțuri {displayName}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          {filtered.length} {filtered.length === 1 ? "anunț activ" : "anunțuri active"}
          {dealer?.city ? ` · ${dealer.city}` : ""}
        </p>

        <div className="mb-6">
          <DealerStockFilters
            filters={filters}
            onChange={setFilters}
            fuelOptions={fuelOptions}
            country="RO"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-72 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground text-sm py-8">
            Niciun anunț nu corespunde filtrelor selectate.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((l) => {
                const photos = Array.isArray(l.photos) ? l.photos : [];
                const firstPhoto = typeof photos[0] === "string" ? photos[0] : photos[0]?.url;
                const slug = buildListingSlug({
                  id: l.id,
                  make: l.make,
                  model: l.model,
                  year: l.year ?? undefined,
                  location_city: l.location_city,
                });
                const mileageKm =
                  l.mileage_unit === "mi" && l.mileage_value
                    ? Math.round(Number(l.mileage_value) * 1.609)
                    : Number(l.mileage_value || 0);
                return (
                  <ModernListingCard
                    key={l.id}
                    id={l.id}
                    title={l.title || `${l.make ?? ""} ${l.model ?? ""}`.trim()}
                    price={Number(l.price || 0)}
                    photo={firstPhoto}
                    images={photos.filter((p: any) => typeof p === "string") as string[]}
                    year={Number(l.year || 0)}
                    mileageKm={mileageKm}
                    fuel_type={l.fuel_type ?? undefined}
                    transmission={l.transmission ?? undefined}
                    location={l.location_city || ""}
                    location_city={l.location_city ?? undefined}
                    slug={slug}
                    make={l.make ?? undefined}
                    model={l.model ?? undefined}
                    created_at={l.created_at ?? undefined}
                  />
                );
              })}
            </div>
            {hasMore && (
              <div className="mt-8 text-center">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-6 py-3 rounded-full border border-border text-sm font-semibold hover:bg-muted disabled:opacity-60"
                >
                  {loadingMore ? "Se încarcă..." : "Încarcă mai multe"}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <SEOFooterContent />
    </div>
  );
}
